import { google, youtube_v3 } from 'googleapis';
import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/**
 * YouTube Data API integration
 */
export class YouTubeIntegration extends BaseIntegration {
  private client: youtube_v3.Youtube;

  constructor(config: IntegrationConfig) {
    super(config);

    const apiKey = config.env.YOUTUBE_API_KEY;
    if (!apiKey) {
      throw new Error('YOUTUBE_API_KEY not configured');
    }

    this.client = google.youtube({
      version: 'v3',
      auth: apiKey,
    });

    this.logger.info('YouTube integration initialized');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    let retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'search':
          data = await this.executeWithRetry(() => this.search(params));
          break;
        case 'getVideo':
          data = await this.executeWithRetry(() => this.getVideo(params));
          break;
        case 'getChannel':
          data = await this.executeWithRetry(() => this.getChannel(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async search(params: IntegrationParams) {
    const { query, maxResults, type } = params;

    this.logger.debug('Searching YouTube', { query: String(query ?? ''), maxResults: Number(maxResults ?? 10), type: String(type ?? '') });

    const response = await this.client.search.list({
      part: ['snippet'],
      q: query as string,
      maxResults: (maxResults as number) || 10,
      type: type ? [type as string] : undefined,
    });

    return response.data.items?.map((item) => ({
      videoId: item.id?.videoId,
      title: item.snippet?.title,
      thumbnail: item.snippet?.thumbnails?.default?.url,
      description: item.snippet?.description,
    }));
  }

  private async getVideo(params: IntegrationParams) {
    const { videoId } = params;

    this.logger.debug('Getting video details', { videoId: String(videoId ?? '') });

    const response = await this.client.videos.list({
      part: ['snippet', 'statistics'],
      id: [videoId as string],
    });

    const video = response.data.items?.[0];
    if (!video) {
      throw new Error(`Video not found: ${videoId}`);
    }

    return {
      title: video.snippet?.title,
      description: video.snippet?.description,
      viewCount: video.statistics?.viewCount,
      likeCount: video.statistics?.likeCount,
    };
  }

  private async getChannel(params: IntegrationParams) {
    const { channelId } = params;

    this.logger.debug('Getting channel details', { channelId: String(channelId ?? '') });

    const response = await this.client.channels.list({
      part: ['snippet', 'statistics'],
      id: [channelId as string],
    });

    const channel = response.data.items?.[0];
    if (!channel) {
      throw new Error(`Channel not found: ${channelId}`);
    }

    return {
      name: channel.snippet?.title,
      description: channel.snippet?.description,
      subscriberCount: channel.statistics?.subscriberCount,
    };
  }
}

// Auto-register
registerIntegration('youtube', YouTubeIntegration);
